/**
 * Note Editor Component
 *
 * Markdown editor with formatting toolbar:
 * - Bold, Italic, Headers
 * - Lists, Code blocks
 * - Tag management
 */

import { useState, useCallback, type KeyboardEvent } from 'react';
import type { Note } from '@/pages/Notes';
import './NoteEditor.css';

interface NoteEditorProps {
  note: Note;
  isNew: boolean;
  onSave: (note: Note) => void;
  onDelete: (noteId: string) => void;
  onCancel: () => void;
  availableTags: string[];
}

type FormatAction = 'bold' | 'italic' | 'h1' | 'h2' | 'bullet' | 'number' | 'code' | 'link';

// Predefined tags that are always available
const PREDEFINED_TAGS = ['planning', 'important', 'personal', 'random'];

export function NoteEditor({
  note,
  isNew,
  onSave,
  onDelete,
  onCancel,
  availableTags,
}: NoteEditorProps): JSX.Element {
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [tags, setTags] = useState<string[]>(note.tags);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');

  const handleFormat = useCallback((action: FormatAction) => {
    const textarea = document.getElementById('note-content') as HTMLTextAreaElement | null;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    let newText = '';
    let cursorOffset = 0;

    switch (action) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        cursorOffset = selectedText ? 0 : -2;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        cursorOffset = selectedText ? 0 : -1;
        break;
      case 'h1':
        newText = `# ${selectedText || 'Heading 1'}`;
        break;
      case 'h2':
        newText = `## ${selectedText || 'Heading 2'}`;
        break;
      case 'bullet':
        newText = `- ${selectedText || 'List item'}`;
        break;
      case 'number':
        newText = `1. ${selectedText || 'List item'}`;
        break;
      case 'link':
        newText = `[${selectedText || 'link text'}](url)`;
        cursorOffset = selectedText ? -1 : -5;
        break;
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }, [content]);

  const handleSave = useCallback((): void => {
    onSave({
      ...note,
      title: title || 'Untitled',
      content,
      tags,
    });
  }, [note, title, content, tags, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        handleFormat('bold');
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        handleFormat('italic');
      }
    },
    [handleFormat, handleSave]
  );

  const handleAddTag = (tag: string): void => {
    const trimmedTag = tag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
    }
    setNewTagInput('');
    setShowTagDropdown(false);
  };

  const handleNewTagKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(newTagInput);
    }
  };

  const handleRemoveTag = (tag: string): void => {
    setTags(tags.filter((t) => t !== tag));
  };

  return (
    <div className="note-editor">
      <div className="note-editor__header">
        <button
          type="button"
          className="note-editor__back"
          onClick={onCancel}
        >
          ← Back
        </button>
        <div className="note-editor__actions">
          {!isNew && (
            <button
              type="button"
              className="note-editor__delete"
              onClick={() => onDelete(note.id)}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="note-editor__save"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>

      <input
        type="text"
        className="note-editor__title"
        placeholder="Note title..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus={isNew}
      />

      <div className="note-editor__tags">
        {tags.map((tag) => (
          <span key={tag} className="note-editor__tag">
            {tag}
            <button
              type="button"
              className="note-editor__tag-remove"
              onClick={() => handleRemoveTag(tag)}
            >
              ×
            </button>
          </span>
        ))}
        <div className="note-editor__tag-add-wrapper">
          <button
            type="button"
            className="note-editor__tag-add"
            onClick={() => setShowTagDropdown(!showTagDropdown)}
          >
            + Add tag
          </button>
          {showTagDropdown && (
            <div className="note-editor__tag-dropdown">
              <input
                type="text"
                className="note-editor__tag-input"
                placeholder="Type to create tag..."
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleNewTagKeyDown}
                autoFocus
              />
              {/* Predefined tags first */}
              {PREDEFINED_TAGS
                .filter((tag) => !tags.includes(tag))
                .filter((tag) => !newTagInput || tag.toLowerCase().includes(newTagInput.toLowerCase()))
                .map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="note-editor__tag-option note-editor__tag-option--predefined"
                    onClick={() => handleAddTag(tag)}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </button>
                ))}
              {/* Custom tags from user's history */}
              {availableTags
                .filter((tag) => !PREDEFINED_TAGS.includes(tag))
                .filter((tag) => !tags.includes(tag))
                .filter((tag) => !newTagInput || tag.toLowerCase().includes(newTagInput.toLowerCase()))
                .map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="note-editor__tag-option"
                    onClick={() => handleAddTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              {/* Create new custom tag option */}
              {newTagInput.trim() &&
               !PREDEFINED_TAGS.includes(newTagInput.trim().toLowerCase()) &&
               !availableTags.includes(newTagInput.trim().toLowerCase()) && (
                <button
                  type="button"
                  className="note-editor__tag-option note-editor__tag-option--new"
                  onClick={() => handleAddTag(newTagInput)}
                >
                  Create "{newTagInput.trim()}"
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="note-editor__toolbar">
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('bold')}
          title="Bold (Cmd+B)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('italic')}
          title="Italic (Cmd+I)"
        >
          <em>I</em>
        </button>
        <span className="note-editor__tool-divider" />
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('h1')}
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('h2')}
          title="Heading 2"
        >
          H2
        </button>
        <span className="note-editor__tool-divider" />
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('bullet')}
          title="Bullet list"
        >
          •
        </button>
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('number')}
          title="Numbered list"
        >
          1.
        </button>
        <span className="note-editor__tool-divider" />
        <button
          type="button"
          className="note-editor__tool"
          onClick={() => handleFormat('link')}
          title="Link"
        >
          🔗
        </button>
      </div>

      <textarea
        id="note-content"
        className="note-editor__content"
        placeholder="Start writing in Markdown..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      <div className="note-editor__hint">
        <span>Cmd+S to save</span>
        <span>Cmd+B bold</span>
        <span>Cmd+I italic</span>
      </div>
    </div>
  );
}
